import { Amplify } from "aws-amplify";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import type { AuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { trackEventWithoutHooks } from "hooks/useAnalytics";
import { restoreSerializedAttribution } from "utils/analytics";
import { AnalyticsEvents } from "types/analytics";
import { getPendingAuthMethod } from "./pendingAuthMethod";

// Substrings returned by Cognito in the OAuth error_description query param.
export const PRESIGNUP_ERROR_MARKER =
  "Please login with your email and password";
export const ACCOUNT_LOCKED_ERROR_MARKER = "ACCOUNT_LOCKED";

export const OAUTH_ERROR_SESSION_KEY = "oauth_error_description";

/**
 * Maps a Cognito OAuth error_description to a typed error category.
 * Used by the Hub listener (early, before React renders) and OAuthCallbackPage.
 */
export const getOAuthErrorType = (
  description: string | null
): "email_account_exists" | "account_locked" | "unknown" => {
  if (description?.includes(PRESIGNUP_ERROR_MARKER))
    return "email_account_exists";
  if (description?.includes(ACCOUNT_LOCKED_ERROR_MARKER))
    return "account_locked";
  return "unknown";
};

Hub.listen("auth", ({ payload }) => {
  // Fires on the callback before login() resolves, so identify gets the UTMs
  if (payload.event === "customOAuthState") {
    restoreSerializedAttribution(payload.data);
  }
  if (payload.event === "signInWithRedirect_failure") {
    const provider = getPendingAuthMethod();
    const errorDesc = sessionStorage.getItem(OAUTH_ERROR_SESSION_KEY);
    // Catching here is more robust than in the callback page because
    // it can happen before the callback page is rendered.
    // Don't clear pending auth method to keep it for login page.
    trackEventWithoutHooks(AnalyticsEvents.LoginFailed, {
      login_method: provider ?? undefined,
      reason: "oauth_error",
      error_type: getOAuthErrorType(errorDesc),
    }).catch((error) => {
      console.error(
        "[Analytics] Failed to track signInWithRedirect failure",
        error
      );
    });
  }
});

/**
 * Captures OAuth error_description from URL params before Amplify clears them.
 * Must be called before initOAuth().
 */
export const captureOAuthError = () => {
  if (!globalThis.location?.pathname.startsWith("/auth/callback/")) return;
  const params = new URLSearchParams(globalThis.location.search);
  const errorDescription = params.get("error_description");
  if (errorDescription) {
    sessionStorage.setItem(OAUTH_ERROR_SESSION_KEY, errorDescription);
  }
};

export const refreshOAuthSession = async () => {
  return await getOAuthSession({ forceRefresh: true });
};

export const getOAuthSession = async (options?: { forceRefresh?: boolean }) => {
  const session = await fetchAuthSession(options);
  return session;
};

export const getOAuthSessionAccessToken = (session: AuthSession) => {
  return session.tokens?.accessToken?.toString();
};

export const checkOAuthSession = async () => {
  const session = await getOAuthSession();
  if (!getOAuthSessionAccessToken(session)) {
    throw new Error("No active session — user is not authenticated");
  }
  return session;
};

export const signOutFromOAuth = async () => {
  // Revokes the Cognito refresh token, then redirects through Cognito /logout back to redirectSignOut
  await signOut();
};

export const initOAuth = () => {
  const redirectSignIn = [`${globalThis.origin}/auth/callback/google`];
  const redirectSignOut = [`${globalThis.origin}`];

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
        loginWith: {
          oauth: {
            domain: import.meta.env.VITE_COGNITO_DOMAIN,
            scopes: [
              "openid",
              "email",
              "profile",
              // allow admin to delete user
              "aws.cognito.signin.user.admin",
            ],
            redirectSignIn,
            redirectSignOut,
            responseType: "code",
          },
          username: true,
        },
      },
    },
  });
};
